import { useMemo, useState } from "react";
import { AlertTriangle, Plus, Sparkles } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { DataTable, type DataTableColumn } from "@/components/cinch/DataTable/DataTable";
import { FilterBar, MultiSelectChip } from "@/components/cinch/FilterBar";
import { KpiTile } from "@/components/cinch/KpiTile";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { useCan } from "@/lib/auth";

import {
  ARCHETYPE_LABEL,
  COVERAGE_LABEL,
  ROLE_LABEL,
  fmtDate,
  fmtMoney2,
  fmtRate,
  useContractCoverage,
  type ContractCoverage,
  type Coverage,
} from "../api";
import { BulkAssignDialog } from "./BulkAssignDialog";

/**
 * The commission worklist: every contract still inside its payout window, by
 * sign date, showing who is assigned to it.
 *
 * This is the entry point the per-contract ledger could never be — the ledger
 * answers "what does THIS contract pay?" but only once you already know which
 * contract to look up. The question that actually starts the work is "which
 * live contracts has nobody been assigned to?", and that needs the whole
 * roster overlaid with the assignments.
 */

const COVERAGE_ORDER: Coverage[] = ["unassigned", "partial", "pending", "covered"];

export function ContractsView() {
  const isAdmin = useCan("commissions.admin");
  const { data, error, isLoading } = useContractCoverage();
  const [search, setSearch] = useState("");
  const [coverage, setCoverage] = useState<string[]>([]);
  const [archetypes, setArchetypes] = useState<string[]>([]);
  const [assignTarget, setAssignTarget] = useState<ContractCoverage | null>(null);

  // Memoized so the filter/option memos below don't recompute every render on a
  // freshly-allocated empty array.
  const all = useMemo(() => data?.contracts ?? [], [data]);

  const archetypeOptions = useMemo(() => {
    const seen = new Map<string, number>();
    for (const c of all) {
      const k = c.contract_archetype ?? "unmapped";
      seen.set(k, (seen.get(k) ?? 0) + 1);
    }
    return [...seen.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([k, n]) => ({
        value: k,
        label: `${k === "unmapped" ? "Unmapped type" : ARCHETYPE_LABEL[k as never] ?? k} (${n})`,
      }));
  }, [all]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter((c) => {
      if (coverage.length > 0 && !coverage.includes(c.coverage)) return false;
      if (
        archetypes.length > 0 &&
        !archetypes.includes(c.contract_archetype ?? "unmapped")
      ) {
        return false;
      }
      if (!q) return true;
      return [
        c.company_name,
        c.company_name_dba,
        c.contract_type_name,
        c.closer_name,
        c.deal_owner_email,
        ...c.payees.map((p) => p.payee_name),
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [all, search, coverage, archetypes]);

  const summary = data?.summary;
  const hasFilters = coverage.length > 0 || archetypes.length > 0;

  const columns: DataTableColumn<ContractCoverage>[] = [
    {
      key: "company_name",
      header: "Company",
      sortable: true,
      render: (r) => (
        <div className="min-w-0">
          <div className="truncate font-medium">{r.company_name ?? "—"}</div>
          {r.company_name_dba && (
            <div className="truncate text-xs text-muted-foreground">
              dba {r.company_name_dba}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "contract_type_name",
      header: "Type",
      sortable: true,
      render: (r) => (
        <div className="text-sm">
          {r.contract_type_name ?? "—"}
          {r.contract_archetype == null && (
            // An unmapped type can't be priced by the plan — say so here rather
            // than letting the recommendation come back mysteriously empty.
            <Badge variant="warning" className="ml-1.5">
              unmapped
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: "contract_signed_at",
      header: "Signed",
      sortable: true,
      render: (r) => (
        <span className="whitespace-nowrap tabular-nums">
          {fmtDate(r.contract_signed_at)}
        </span>
      ),
    },
    {
      key: "mrr_total",
      header: "MRR",
      sortable: true,
      numeric: true,
      align: "right",
      render: (r) => (
        <span className="tabular-nums">{fmtMoney2(r.mrr_total)}</span>
      ),
    },
    {
      key: "closer_name",
      header: "Closer (per HubSpot)",
      sortable: true,
      render: (r) => <CloserCell row={r} />,
    },
    {
      key: "payees",
      header: "Assigned",
      render: (r) => <AssignedCell row={r} />,
    },
    {
      key: "coverage",
      header: "Coverage",
      sortable: true,
      render: (r) => (
        <StatusBadge value={r.coverage} label={COVERAGE_LABEL[r.coverage]} />
      ),
    },
  ];

  if (isAdmin) {
    columns.push({
      key: "actions",
      header: "",
      align: "right",
      render: (r) => (
        <Button
          size="sm"
          variant={r.coverage === "unassigned" ? "default" : "ghost"}
          onClick={(e) => {
            e.stopPropagation();
            setAssignTarget(r);
          }}
        >
          {r.coverage === "unassigned" ? (
            <>
              <Sparkles className="mr-1.5 h-4 w-4" />
              Assign
            </>
          ) : (
            <>
              <Plus className="mr-1.5 h-4 w-4" />
              Add
            </>
          )}
        </Button>
      ),
    });
  }

  return (
    <div className="flex flex-col gap-4">
      {error != null && (
        <Alert variant="danger">
          <AlertTitle>Couldn't load the contract roster</AlertTitle>
          <AlertDescription>
            {error instanceof Error ? error.message : String(error)}
          </AlertDescription>
        </Alert>
      )}

      {data?.warning && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>No approved comp plan</AlertTitle>
          <AlertDescription>{data.warning}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <KpiTile
          label="Live contracts"
          value={summary ? summary.contracts : "—"}
          sub="still inside the M1–M12 + P1 window"
          info="Contracts that are not cancelled, are still billing, and whose first billed month is within the last 13 months — the only ones that can still owe a commission."
          infoTitle="What counts as live"
        />
        <KpiTile
          label="Nobody assigned"
          value={summary ? summary.unassigned : "—"}
          tone={summary && summary.unassigned > 0 ? "danger" : "positive"}
          sub={summary ? `${fmtMoney2(summary.mrr_unassigned)}/mo unassigned` : undefined}
          onClick={() => setCoverage(coverage.includes("unassigned") ? [] : ["unassigned"])}
          selected={coverage.includes("unassigned")}
        />
        <KpiTile
          label="Missing a paid role"
          value={summary ? summary.partial : "—"}
          tone={summary && summary.partial > 0 ? "warning" : "neutral"}
          sub="somebody is assigned, a plan-priced role isn't"
          onClick={() => setCoverage(coverage.includes("partial") ? [] : ["partial"])}
          selected={coverage.includes("partial")}
        />
        <KpiTile
          label="Pending approval"
          value={summary ? summary.pending : "—"}
          tone={summary && summary.pending > 0 ? "warning" : "neutral"}
          sub="off-plan rates awaiting sign-off"
          onClick={() => setCoverage(coverage.includes("pending") ? [] : ["pending"])}
          selected={coverage.includes("pending")}
        />
        <KpiTile
          label="Fully assigned"
          value={summary ? summary.covered : "—"}
          tone="positive"
          sub={data?.plan_name ? `against ${data.plan_name}` : undefined}
          onClick={() => setCoverage(coverage.includes("covered") ? [] : ["covered"])}
          selected={coverage.includes("covered")}
        />
      </div>

      <FilterBar>
        <MultiSelectChip
          label="Coverage"
          options={COVERAGE_ORDER.map((c) => ({
            value: c,
            label: COVERAGE_LABEL[c],
          }))}
          value={coverage}
          onChange={setCoverage}
        />
        <MultiSelectChip
          label="Contract type"
          options={archetypeOptions}
          value={archetypes}
          onChange={setArchetypes}
        />
        <div className="flex-1" />
        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setCoverage([]);
              setArchetypes([]);
            }}
          >
            Clear filters
          </Button>
        )}
      </FilterBar>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.contract_id}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search company, closer, or assigned payee…"
        loading={isLoading}
        emptyMessage={
          hasFilters || search
            ? "No contracts match these filters."
            : "No live contracts in the payout window."
        }
        renderExpanded={(r) => <ContractDetail row={r} />}
      />

      {assignTarget && (
        <BulkAssignDialog
          open
          onOpenChange={(o) => {
            if (!o) setAssignTarget(null);
          }}
          contract={assignTarget}
        />
      )}
    </div>
  );
}

/** Who HubSpot says closed it, and how confident that is. */
function CloserCell({ row }: { row: ContractCoverage }) {
  if (!row.closer_name && !row.deal_owner_email) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="min-w-0 text-sm">
            <div className="truncate">{row.closer_name ?? row.deal_owner_email}</div>
            <div className="flex items-center gap-1">
              {row.bdr_owner_email && (
                <Badge variant="info" className="px-1 py-0 text-[10px]">
                  BDR
                </Badge>
              )}
              {row.partner_source_detail && (
                <Badge variant="info" className="px-1 py-0 text-[10px]">
                  partner
                </Badge>
              )}
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div className="max-w-xs space-y-0.5 text-xs">
            {row.closer_name && <p>Contract closer: {row.closer_name}</p>}
            {row.deal_owner_email && <p>HubSpot deal owner: {row.deal_owner_email}</p>}
            {row.bdr_owner_email && <p>BDR on the deal: {row.bdr_owner_email}</p>}
            {row.partner_source_detail && (
              <p>Partner source: {row.partner_source_detail}</p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** The assignment chips, plus the roles the plan pays that nobody holds. */
function AssignedCell({ row }: { row: ContractCoverage }) {
  if (row.payees.length === 0 && row.missing_roles.length === 0) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }
  return (
    <div className="flex flex-wrap items-center gap-1">
      {row.payees.map((p) => (
        <Badge
          key={p.assignment_id}
          variant={p.status === "pending_approval" ? "warning" : "neutral"}
        >
          {p.payee_name}
          <span className="ml-1 opacity-70">
            {ROLE_LABEL[p.role]}{" "}
            {p.fixed_monthly_amount != null
              ? `${fmtMoney2(p.fixed_monthly_amount)}/mo`
              : fmtRate(p.rate_pct)}
          </span>
        </Badge>
      ))}
      {row.missing_roles.map((r) => (
        <Badge key={r} variant="warning" className="border-dashed">
          no {ROLE_LABEL[r]}
        </Badge>
      ))}
      {row.payees.length === 0 && row.assignment_count > 0 && (
        // Row-level read scope: somebody IS assigned, just not you.
        <span className="text-xs text-muted-foreground">
          {row.assignment_count} assignment
          {row.assignment_count === 1 ? "" : "s"} (not yours)
        </span>
      )}
    </div>
  );
}

function ContractDetail({ row }: { row: ContractCoverage }) {
  return (
    <div className="grid gap-4 px-2 py-1 text-sm sm:grid-cols-3">
      <div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Contract
        </p>
        <dl className="space-y-0.5">
          <Row label="Contract id" value={row.contract_id} mono />
          <Row label="Type" value={row.contract_type_name ?? "—"} />
          <Row
            label="Plan key"
            value={
              row.contract_archetype
                ? ARCHETYPE_LABEL[row.contract_archetype]
                : "no archetype — the plan can't price this"
            }
          />
          <Row label="Signed" value={fmtDate(row.contract_signed_at)} />
          <Row label="First bill" value={fmtDate(row.billing_effective_from)} />
          <Row label="MRR" value={fmtMoney2(row.mrr_total)} />
        </dl>
      </div>
      <div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Attribution (HubSpot)
        </p>
        <dl className="space-y-0.5">
          <Row label="Closer" value={row.closer_name ?? "—"} />
          <Row label="Deal owner" value={row.deal_owner_email ?? "—"} />
          <Row label="BDR" value={row.bdr_owner_email ?? "—"} />
          <Row label="Partner source" value={row.partner_source_detail ?? "—"} />
          <Row label="Deal id" value={row.hubspot_deal_id ?? "—"} mono />
        </dl>
      </div>
      <div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Assignments ({row.assignment_count})
        </p>
        {row.payees.length === 0 ? (
          <p className="text-muted-foreground">
            {row.assignment_count > 0
              ? "Assigned, but not to you."
              : "Nobody is assigned to this contract."}
          </p>
        ) : (
          <ul className="space-y-1">
            {row.payees.map((p) => (
              <li key={p.assignment_id} className="flex items-center gap-2">
                <span className="font-medium">{p.payee_name}</span>
                <span className="text-muted-foreground">{ROLE_LABEL[p.role]}</span>
                <span className="tabular-nums">
                  {p.fixed_monthly_amount != null
                    ? `${fmtMoney2(p.fixed_monthly_amount)}/mo`
                    : fmtRate(p.rate_pct)}
                </span>
                {p.off_plan && <Badge variant="warning">off-plan</Badge>}
              </li>
            ))}
          </ul>
        )}
        {row.missing_roles.length > 0 && (
          <p className="mt-2 text-warning">
            The plan also pays{" "}
            {row.missing_roles.map((r) => ROLE_LABEL[r]).join(", ")} on this
            contract type — nobody is assigned.
          </p>
        )}
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex gap-2">
      <dt className="w-24 shrink-0 text-muted-foreground">{label}</dt>
      <dd className={mono ? "break-all font-mono text-xs" : "min-w-0 break-words"}>
        {value}
      </dd>
    </div>
  );
}
